import React, { useCallback, useContext } from 'react'
import DoublePointTakeoverHeroSection from './DoublePointTakeoverHeroSection'
import OfferCardCarouselSection from 'components/Common/OfferCardCarousel/OfferCardCarouselSection'
import { encodeSearchParams } from 'lib/search/searchUtils'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import config from 'constants/config'
import { useCurrentSiteTakeoverV2 } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { SOCIETE_DOUBLE_POINTS_TAKEOVER_ID } from '../constants/societeDoublePointsTakeoverConstants'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import HttpStatus from 'components/Common/HttpStatus'
import LoyaltyLandingPageMovingTiersSection from 'loyalty/pages/loyaltyLandingPageSharedSections/LoyaltyLandingPageMovingTiersSection'
import LoyaltyLandingPageBenefitsTableSection from 'loyalty/pages/loyaltyLandingPageSharedSections/LoyaltyLandingPageBenefitsTableSection'
import ModalContext from 'contexts/ModalContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import AccountAccessModal, { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal'
import { selectLoggedIn } from 'selectors/accountSelectors'
import enrolLoyaltyAccount from 'actions/loyalty/enrolLoyaltyAccount'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'

const hotelFilters: App.OfferListFilters = {
  offerTypes: ['hotel', 'tactical_ao_hotel'],
  limit: 20,
  frontPageVisibleOnly: true,
}

const hotelSearchQuery = encodeSearchParams({
  searchItem: ANYWHERE_SEARCH_ITEM,
  rooms: [config.search.defaultOccupants],
})

const tourFilters: App.OfferListFilters = {
  offerTypes: ['tour_v2'],
  limit: 20,
  frontPageVisibleOnly: true,
  excludeConnectionTours: true,
}

const tourSearchQuery = encodeSearchParams({
  searchItem: ANYWHERE_SEARCH_ITEM,
  rooms: [config.search.defaultOccupants],
})

const cruiseFilters: App.OfferListFilters = {
  offerTypes: ['cruise'],
  limit: 20,
  sortBy: 'vertical-cruise',
  frontPageVisibleOnly: true,
}

const cruiseSearchQuery = encodeSearchParams({
  searchItem: ANYWHERE_SEARCH_ITEM,
  rooms: [config.search.defaultOccupants],
})

function DoublePointTakeoverLandingPage() {
  const currentTakeover = useCurrentSiteTakeoverV2()
  const showModal = useContext(ModalContext)
  const dispatch = useAppDispatch()
  const isLoggedIn = useAppSelector(state => selectLoggedIn(state))
  const [tier] = useCurrentLoyaltyTier()
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const onJoinClick = useCallback(async() => {
    let loginResult: AccountAccessModalResult = { loggedIn: isLoggedIn }

    if (!isLoggedIn) {
      loginResult = await showModal<AccountAccessModalResult>(<AccountAccessModal initialMode="loyaltyLogin" />)
    }
    if (loginResult.loggedIn) {
      dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
    }
  }, [dispatch, isLoggedIn, showModal])

  const highlightTier = hasLoyaltyAccount ? tier : undefined

  if (currentTakeover?.id !== SOCIETE_DOUBLE_POINTS_TAKEOVER_ID) {
    // page is only meant for when the double points campaign is active
    return <HttpStatus code={404}>
      <Error404PageWithOffers />
    </HttpStatus>
  }

  return <div>
    <DoublePointTakeoverHeroSection onJoin={onJoinClick} />
    <OfferCardCarouselSection
      title={<>Double status credits on <i>hotels</i> </>}
      filters={hotelFilters}
      allLink={`/search?${hotelSearchQuery.toString()}`}
      hideWhenEmpty
      tileStyle="card-default"
      tileSize="medium"
    />
    <OfferCardCarouselSection
      title={<>Double status credits on <i>tours</i></>}
      filters={tourFilters}
      allLink={`/search/tours?${tourSearchQuery.toString()}`}
      hideWhenEmpty
      tileStyle="card-default"
      tileSize="medium"
    />
    <OfferCardCarouselSection
      title={<>Double status credits on <i>cruises</i></>}
      filters={cruiseFilters}
      allLink={`/search/cruises?${cruiseSearchQuery.toString()}`}
      hideWhenEmpty
      tileStyle="card-default"
      tileSize="medium"
    />
    <LoyaltyLandingPageMovingTiersSection />
    <LoyaltyLandingPageBenefitsTableSection
      highlightTier={highlightTier}
      ctaLink={tier === 'platinum' ? '/societe#about-benefits' : '/societe#benefits-your-benefits'}
      ctaText="See your benefits"
    />
  </div>
}

export default DoublePointTakeoverLandingPage
